import clsx from 'clsx';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import Divider from '@material-ui/core/Divider';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import Dialog from 'components/DialogWrapper/DialogWrapper';

import Typography from '@material-ui/core/Typography';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import CalendarEditIcon from 'mdi-material-ui/CalendarEdit';
import CalendarRemoveIcon from 'mdi-material-ui/CalendarRemove';

import Button from 'components/Button/Button';
import ButtonSwitch from 'components/ButtonSwitch/ButtonSwitch';
import CountrySelect from 'components/CountrySelect/CountrySelect';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import InTime from 'components/InTime/InTime';
import TextInput from 'components/TextInput/TextInput';
import AssignmentsSelect from './AssignmentsSelect';
import useStyles from './RecordDialogLayoutStyles';

import { RECORD_STATUS } from 'LixaniAPI/enums';

import MapDialog from 'components/MapDialog/MapDialog';
import ComboBox from 'scenes/ExpenseTracking/components/ComboBox';

import { selectProjectAssignments } from 'store/assignments/selectors/assignment.selectors';
import {
  closeEditRecordDialog,
  copyRecordAsNew,
  setEditRecordDialogAttributes,
} from 'store/records/actions/records.actions';
import {
  isPermissionToApproveSelectedRecord,
  isPermissionToCheckSelectedRecordBilledTimes,
  isPermissionToCheckSelectedRecordClockedTimes,
  isPermissionsToEditSelectedRecord,
  selectEditDialogIsRecordAttributeChanges,
  selectEditRecordDialogData,
  selectIsBusinessPermissionToEditRecord,
  selectIsRecordHistoryLoading,
  selectIsSelectedRecordPartOfApprovalGroup,
  selectRecordEditPending,
  selectRecordStatusChangePending,
  selectSelectedRecordApproveStatus,
  selectSelectedRecordCheckStatus,
  selectSelectedRecordCheckTimes,
} from 'store/records/selectors/records.selectors';
import { TypeChange } from './typeChange';

import { CircularProgress, TextField } from '@material-ui/core';
import { deleteRecordThunk } from 'store/records/thunks/deleteRecordThunk';
import { editRecord } from 'store/records/thunks/editRecord';
import { HistoryList } from './historyList';

import ButtonLoading from 'components/ButtonLoading/ButtonLoading';
import DatePickerTypeable from 'components/DatePickerTypeable/DatePickerTypeable';
import { OpenJoinEmployeeToProjectDialogButton } from 'components/JoinExistingEmployeeDialog/OpenJoinEmployeeToProjectDialogButton';
import { useHistory } from 'react-router-dom';
import { RESOURCES_ADD_EMPLOYEES_TO_PROJECTS } from 'scenes/Permissions/constants/permissions';
import { selectIsRequiredBusinessPermissionByBusinessId } from 'scenes/Permissions/store/selectors/personalPermissionsSelectors';
import { selectIsLoadApproversPending } from 'store/approvals/selectors/approvals.selectors';
import { selectEditDialogProjectOptions } from 'store/projects/selectors/recordProjectOptions.selectors';
import { selectIsCreatingAllowance } from 'store/recordAllowances/selectors/record-allowances-base.selectors';
import { selectSelectedRecordProject } from 'store/records/selectors/selectedRecords.selectors';
import { handleBreakRecordsStatusChange } from 'store/records/thunks/handleBreakRecordsStatusChange';
import { handleChangeRecordEditProject } from 'store/records/thunks/handleChangeRecordEditProject.thunk';
import { loadRecordHistory } from 'store/records/thunks/loadRecordHistory';
import { selectUserActiveEmployerBusinessId } from 'store/user/selectors.js/user.selectors';
import { returnIsFirefoxOrSafari } from 'utils/returnIsFirefoxOrSafari.util';
import { BreaksComponent } from './BreaksComponent';
import { CheckSingleTimeCheckBox } from './CheckSingleTimeCheckBox';
import { ProjectResourcesSelect } from './ProjectResourcesSelect/ProjectResourcesSelect';
import { RecordAllowances } from './RecordAllowances/RecordAllowances';
import { ApprovalComponent } from './approvalComponent';
import { ApproverList } from './approverList';
import { NewHistoryList } from './newHistoryList';

function EditRecordDialog({ open }) {
  const dispatch = useDispatch();
  const isPermissionsToEdit = useSelector(isPermissionsToEditSelectedRecord);
  const isPermissionToApprove = useSelector(
    isPermissionToApproveSelectedRecord
  );

  const isPermissionToCheckClockedTimes = useSelector(
    isPermissionToCheckSelectedRecordClockedTimes
  );

  const isPermissionToCheckBilledTimes = useSelector(
    isPermissionToCheckSelectedRecordBilledTimes
  );

  const dialogData = useSelector(selectEditRecordDialogData);
  const {
    id,
    project,
    clockInAt,
    clockOutAt,
    billedClockInAt,
    billedClockOutAt,
    status,
    type,
    subType,
    description,
    resource,
    assignment,
    targetCountry,
    latIn,
    latOut,
    longIn,
    longOut,
    assignmentUnitAmount,
  } = dialogData;

  const history = useHistory();

  const classes = useStyles();
  const theme = useTheme();
  const upSm = useMediaQuery(theme.breakpoints.up('sm'));
  const [removeRecordDialog, setRemoveRecordDialog] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  const { t } = useTranslation('common');

  const approved = useSelector(selectSelectedRecordApproveStatus);
  const sentToApproval = useSelector(selectIsSelectedRecordPartOfApprovalGroup);
  const checked = useSelector(selectSelectedRecordCheckStatus);
  const statusChangePending = useSelector(selectRecordStatusChangePending);
  const recordEditPending = useSelector(selectRecordEditPending);
  const historyLoading = useSelector(selectIsRecordHistoryLoading);
  const isLoadApproversPending = useSelector(selectIsLoadApproversPending);

  const isCreatingAllowance = useSelector(selectIsCreatingAllowance);

  const updatesPending =
    statusChangePending || recordEditPending || isCreatingAllowance;

  const [showHistory, setShowHistory] = useState(false);
  const checkedOut = status === 'OUT' ? true : false;

  const projectOptions = useSelector(selectEditDialogProjectOptions);

  const isRecordAttributeChanges = useSelector(
    selectEditDialogIsRecordAttributeChanges
  );

  const selectedRecordProject = useSelector(selectSelectedRecordProject);

  const isRecordEditingRestricedForSubcontractors =
    selectedRecordProject?.isSubcontractorsOwnEmployeeRecordEditingRestricted;

  const userActiveEmployerBusinessId = useSelector(
    selectUserActiveEmployerBusinessId
  );

  const isSubcontractorRecordEditingRestrictionEffective =
    isRecordEditingRestricedForSubcontractors &&
    !isPermissionsToEdit &&
    userActiveEmployerBusinessId !== selectedRecordProject.business_id;

  const {
    billedInAtChecked,
    billedOutAtChecked,
    clockedInAtChecked,
    clockedOutAtChecked,
  } = useSelector(selectSelectedRecordCheckTimes);

  const openHistory = () => {
    setShowHistory((prev) => {
      if (!prev) {
        dispatch(loadRecordHistory());
      }
      return !prev;
    });
  };

  const [unitAmountDialogOpen, setUnitAmountDialogOpen] = useState(false);

  const setAssignmentUnitAmount = (value) => {
    setAttributes({ assignmentUnitAmount: value });
  };
  const setAssignmentUnitAmountDialogOpen = (value) => {
    setUnitAmountDialogOpen(value);
  };

  const projectAssignments = useSelector((state) =>
    selectProjectAssignments(state, project)
  );

  const isPermissionToAddEmployees = useSelector((state) =>
    selectedRecordProject?.business_id
      ? selectIsRequiredBusinessPermissionByBusinessId(state, {
          businessId: selectedRecordProject.business_id,
          permission: RESOURCES_ADD_EMPLOYEES_TO_PROJECTS,
        })
      : false
  );

  const isBusinessPermissionToManageRecord = useSelector(
    selectIsBusinessPermissionToEditRecord
  );

  const isRecordApprovedAndUserNotWithBusinessPermissionsToEdit =
    !isBusinessPermissionToManageRecord && approved;

  const handleTypeChange = (value) => {
    setAttributes({ type: value });
  };

  const handleSubTypeChange = (value) => {
    setAttributes({ subType: value });
  };

  const handleSave = () => {
    dispatch(editRecord(dialogData));
  };

  const closeDialog = () => {
    dispatch(closeEditRecordDialog());
  };

  const setAttributes = (attributeObject) => {
    dispatch(setEditRecordDialogAttributes(attributeObject));
  };

  const debounceDescriptionSearch = useCallback(
    debounce(setDescriptionSearchText, 500),
    []
  );

  function setDescriptionSearchText(value) {
    setAttributes({ description: value });
  }

  const handleClockInGreaterThanClockout = (clockIn, clockOut) => {
    if (clockIn > clockOut) {
      setAttributes({ clockInAt: clockIn, clockOutAt: clockIn });
    }
  };

  const handleBilledClockInGreaterThanClockout = (clockIn, clockOut) => {
    if (clockIn > clockOut) {
      setAttributes({ billedClockInAt: clockIn, billedClockOutAt: clockIn });
    }
  };

  const handleProjectChange = (projectId) => {
    dispatch(
      handleChangeRecordEditProject({
        prevProjectId: project,
        newProjectId: projectId,
        prevResourceId: resource,
      })
    );
  };

  const handleAssignmentChange = (assignmentId) => {
    // handling assignment deselect on click if assignment already selected
    if (assignmentId !== assignment) {
      setAttributes({
        assignment: assignmentId,
        assignmentUnitAmount: undefined,
      });
    } else {
      setAttributes({
        assignment: undefined,
        assignmentUnitAmount: undefined,
      });
    }
  };

  const handleRecordDelete = () => {
    dispatch(deleteRecordThunk({ projectId: project, recordId: id }));
    closeDialog();
  };

  const clockedInGreaterThanClockedOut = clockInAt > clockOutAt;

  const billedClockInGreaterThanClockout = billedClockInAt > billedClockOutAt;

  const dateFormat = 'YYYY-MM-DDTHH:mm';

  const formattedClockInAt = moment(clockInAt).format(dateFormat);

  const formattedClockOutAt = moment(clockOutAt).format(dateFormat);

  const formattedBilledClockInAt = moment(billedClockInAt).format(dateFormat);

  const formattedBilledClockOutAt = moment(billedClockOutAt).format(dateFormat);

  const formatDateOutput = (date) => {
    return moment(date).toISOString();
  };

  const handleCheckOutStatus = () => {
    const status = checkedOut ? 'IN' : 'OUT';

    const now = formatDateOutput(moment());
    setAttributes({
      status: status,
      billedClockOutAt: now,
      clockOutAt: now,
    });

    dispatch(handleBreakRecordsStatusChange({ status: status, now: now }));
  };

  const loadingSpinner = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '16px',
      }}
    >
      <CircularProgress color="primary" size={36} />
    </div>
  );

  const isFirefoxOrSafari = returnIsFirefoxOrSafari();

  return (
    <>
      <Dialog
        aria-labelledby="edit-record-dialog"
        classes={{ paper: classes.dialogPaper }}
        disableBackdropClick
        fullScreen={!upSm}
        fullWidth
        maxWidth="sm"
        open={open}
        onClose={closeDialog}
      >
        <DialogHeader
          icon={<CalendarEditIcon />}
          onClose={closeDialog}
          title={t('calendar.edit_record')}
        />
        <DialogContent className={classes.dialogContent}>
          {isSubcontractorRecordEditingRestrictionEffective && (
            <div
              style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                color: '#1f2937',
              }}
            >
              <Typography variant="body2" style={{ color: '#1f2937' }}>
                {t('record.editing_restricted_for_subcontractors')}
              </Typography>
            </div>
          )}
          <Grid container spacing={2} className={classes.gridContainer}>
            {!isLoadApproversPending ? (
              <>
                <Grid item xs={12}>
                  <ApproverList></ApproverList>
                </Grid>
                {isPermissionToCheckClockedTimes || isPermissionToApprove ? (
                  <ApprovalComponent
                    isPermissionToCheck={
                      isPermissionToCheckClockedTimes ||
                      isPermissionToCheckBilledTimes
                    }
                    isPermissionToApprove={isPermissionToApprove}
                    updatesPending={updatesPending}
                    checkedOut={checkedOut}
                    isRecordAttributeChanges={isRecordAttributeChanges}
                    checked={checked}
                    sentToApproval={sentToApproval}
                  ></ApprovalComponent>
                ) : null}
              </>
            ) : (
              <Grid item xs={12} className={classes.centerItems}>
                {loadingSpinner}
              </Grid>
            )}

            {showHistory ? (
              !historyLoading ? (
                <>
                  <HistoryList></HistoryList>
                  <NewHistoryList></NewHistoryList>
                </>
              ) : (
                <Grid item xs={12} className={classes.centerItems}>
                  {loadingSpinner}
                </Grid>
              )
            ) : null}
            <Grid item xs={12} className={classes.gridItem}>
              <Typography className={classes.inTime}>
                IN{' '}
                <InTime
                  clockInAt={formattedClockInAt}
                  clockOutAt={formattedClockOutAt}
                  status={checkedOut ? RECORD_STATUS.OUT : RECORD_STATUS.IN}
                />
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.clockedItem}>
              <ButtonSwitch
                checked={!checkedOut}
                fullWidth
                on={t('record.clocked_in')}
                off={t('record.clocked_out')}
                onChange={handleCheckOutStatus}
                disabled={
                  !isPermissionsToEdit ||
                  approved ||
                  updatesPending ||
                  checked ||
                  sentToApproval ||
                  billedOutAtChecked ||
                  clockedOutAtChecked
                }
              />
            </Grid>
            {/*LEIMATTU AIKA*/}
            <Grid item xs={12}>
              <label className="mylabel">{t('calendar.clocked_time')}</label>
            </Grid>
            <Grid item xs={6}>
              <CheckSingleTimeCheckBox
                isPermissionToCheck={isPermissionToCheckClockedTimes}
                updatesPending={updatesPending}
                checkedOut={checkedOut}
                isRecordAttributeChanges={isRecordAttributeChanges}
                checked={clockedInAtChecked}
                attributeToEditName={'clockedInAtChecked'}
              ></CheckSingleTimeCheckBox>
              {isFirefoxOrSafari ? (
                <DatePickerTypeable
                  label={t('calendar.start_date')}
                  iconVisible
                  showTimeSelect
                  timeIntervals={5}
                  dateFormat="dd.MM.yyyy HH:mm"
                  value={formattedClockInAt}
                  onChange={(date) => {
                    setAttributes({
                      clockInAt: formatDateOutput(date),
                    });
                    handleClockInGreaterThanClockout(
                      formatDateOutput(date),
                      formatDateOutput(clockOutAt)
                    );
                  }}
                  disabled={
                    !isPermissionsToEdit ||
                    isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                    updatesPending ||
                    checked ||
                    sentToApproval ||
                    clockedInAtChecked
                  }
                />
              ) : (
                <TextField
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ className: classes.textInput }}
                  fullWidth
                  label={t('calendar.start_date')}
                  variant="outlined"
                  size="small"
                  type="datetime-local"
                  value={formattedClockInAt}
                  onChange={(event) =>
                    setAttributes({
                      clockInAt: formatDateOutput(event.target.value),
                    })
                  }
                  onBlur={(event) =>
                    handleClockInGreaterThanClockout(
                      formatDateOutput(event.target.value),
                      formatDateOutput(clockOutAt)
                    )
                  }
                  disabled={
                    !isPermissionsToEdit ||
                    isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                    updatesPending ||
                    checked ||
                    sentToApproval ||
                    clockedInAtChecked
                  }
                />
              )}
            </Grid>
            <Grid
              item
              xs={6}
              className={clsx(classes.dateItem, checkedOut && classes.hidden)}
            >
              <Typography className={classes.dateItem}>
                {t('calendar.clocked_in')}
              </Typography>
            </Grid>
            <Grid item xs={6} className={clsx(!checkedOut && classes.hidden)}>
              <CheckSingleTimeCheckBox
                isPermissionToCheck={isPermissionToCheckClockedTimes}
                updatesPending={updatesPending}
                checkedOut={checkedOut}
                isRecordAttributeChanges={isRecordAttributeChanges}
                checked={clockedOutAtChecked}
                attributeToEditName={'clockedOutAtChecked'}
              ></CheckSingleTimeCheckBox>
              {isFirefoxOrSafari ? (
                <DatePickerTypeable
                  label={t('calendar.end_date')}
                  iconVisible
                  showTimeSelect
                  timeIntervals={5}
                  dateFormat="dd.MM.yyyy HH:mm"
                  value={formattedClockOutAt}
                  minDate={new Date(formattedClockInAt)}
                  onChange={(date) => {
                    setAttributes({
                      clockOutAt: formatDateOutput(date),
                    });
                  }}
                  disabled={
                    !isPermissionsToEdit ||
                    isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                    updatesPending ||
                    checked ||
                    sentToApproval ||
                    clockedInAtChecked
                  }
                />
              ) : (
                <TextField
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ className: classes.textInput }}
                  fullWidth
                  label={t('calendar.end_date')}
                  variant="outlined"
                  size="small"
                  type="datetime-local"
                  value={formattedClockOutAt}
                  onChange={(event) =>
                    setAttributes({
                      clockOutAt: formatDateOutput(event.target.value),
                    })
                  }
                  onBlur={(event) =>
                    handleClockInGreaterThanClockout(
                      formatDateOutput(clockInAt),
                      formatDateOutput(event.target.value)
                    )
                  }
                  disabled={
                    !isPermissionsToEdit ||
                    isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                    updatesPending ||
                    checked ||
                    sentToApproval ||
                    clockedOutAtChecked
                  }
                />
              )}
            </Grid>
            {/*LASKUTETTAVA AIKA*/}
            {isPermissionToApprove ? (
              <>
                <Grid item xs={12}>
                  <label className="mylabel">{t('calendar.billed_time')}</label>
                </Grid>
                <Grid item xs={6}>
                  <CheckSingleTimeCheckBox
                    isPermissionToCheck={isPermissionToCheckBilledTimes}
                    updatesPending={updatesPending}
                    checkedOut={checkedOut}
                    isRecordAttributeChanges={isRecordAttributeChanges}
                    checked={billedInAtChecked}
                    attributeToEditName={'billedInAtChecked'}
                  ></CheckSingleTimeCheckBox>
                  {isFirefoxOrSafari ? (
                    <DatePickerTypeable
                      label={t('calendar.start_date')}
                      iconVisible
                      showTimeSelect
                      timeIntervals={5}
                      dateFormat="dd.MM.yyyy HH:mm"
                      value={formattedBilledClockInAt}
                      onChange={(date) => {
                        setAttributes({
                          billedClockInAt: formatDateOutput(date),
                        });
                        handleBilledClockInGreaterThanClockout(
                          formatDateOutput(date),
                          formatDateOutput(billedClockOutAt)
                        );
                      }}
                      disabled={
                        (!isPermissionsToEdit && !isPermissionToApprove) ||
                        isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                        updatesPending ||
                        sentToApproval ||
                        billedInAtChecked
                      }
                    />
                  ) : (
                    <TextField
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ className: classes.textInput }}
                      fullWidth
                      label={t('calendar.start_date')}
                      variant="outlined"
                      size="small"
                      type="datetime-local"
                      value={formattedBilledClockInAt}
                      onChange={(event) =>
                        setAttributes({
                          billedClockInAt: formatDateOutput(event.target.value),
                        })
                      }
                      onBlur={(event) =>
                        handleBilledClockInGreaterThanClockout(
                          formatDateOutput(event.target.value),
                          formatDateOutput(billedClockOutAt)
                        )
                      }
                      disabled={
                        (!isPermissionsToEdit && !isPermissionToApprove) ||
                        isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                        updatesPending ||
                        sentToApproval ||
                        billedInAtChecked
                      }
                    />
                  )}
                </Grid>
                <Grid
                  item
                  xs={6}
                  className={clsx(
                    classes.dateItem,
                    checkedOut && classes.hidden
                  )}
                >
                  <Typography className={classes.clockedText}>
                    {t('calendar.clocked_in')}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={6}
                  className={clsx(!checkedOut && classes.hidden)}
                >
                  <CheckSingleTimeCheckBox
                    isPermissionToCheck={isPermissionToCheckBilledTimes}
                    updatesPending={updatesPending}
                    checkedOut={checkedOut}
                    isRecordAttributeChanges={isRecordAttributeChanges}
                    checked={billedOutAtChecked}
                    attributeToEditName={'billedOutAtChecked'}
                  ></CheckSingleTimeCheckBox>
                  {isFirefoxOrSafari ? (
                    <DatePickerTypeable
                      label={t('calendar.end_date')}
                      iconVisible
                      showTimeSelect
                      timeIntervals={5}
                      dateFormat="dd.MM.yyyy HH:mm"
                      value={formattedBilledClockOutAt}
                      minDate={new Date(formattedBilledClockInAt)}
                      onChange={(date) => {
                        setAttributes({
                          billedClockOutAt: formatDateOutput(date),
                        });
                      }}
                      disabled={
                        (!isPermissionsToEdit && !isPermissionToApprove) ||
                        isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                        updatesPending ||
                        sentToApproval ||
                        billedOutAtChecked
                      }
                    />
                  ) : (
                    <TextField
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ className: classes.textInput }}
                      fullWidth
                      label={t('calendar.end_date')}
                      variant="outlined"
                      size="small"
                      type="datetime-local"
                      value={formattedBilledClockOutAt}
                      onChange={(event) =>
                        setAttributes({
                          billedClockOutAt: formatDateOutput(
                            event.target.value
                          ),
                        })
                      }
                      onBlur={(event) =>
                        handleBilledClockInGreaterThanClockout(
                          formatDateOutput(billedClockInAt),
                          formatDateOutput(event.target.value)
                        )
                      }
                      disabled={
                        (!isPermissionsToEdit && !isPermissionToApprove) ||
                        isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                        updatesPending ||
                        sentToApproval ||
                        billedOutAtChecked
                      }
                    />
                  )}
                </Grid>
              </>
            ) : null}
            <BreaksComponent
              disabled={
                !isPermissionsToEdit ||
                isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                updatesPending
              }
              addingBreaksDisabled={
                sentToApproval ||
                isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                updatesPending ||
                isSubcontractorRecordEditingRestrictionEffective
              }
              parentClockIn={formattedClockInAt}
              parentClockOut={formattedClockOutAt}
            ></BreaksComponent>
            <RecordAllowances
              disabled={
                !isPermissionsToEdit ||
                isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                updatesPending
              }
              addingAllowancesDisabled={
                sentToApproval ||
                isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                updatesPending
              }
              parentClockIn={formattedClockInAt}
              parentClockOut={formattedClockOutAt}
            />
            <Grid item xs={12} className={classes.gridItem}>
              <ComboBox
                items={projectOptions}
                selected={project}
                onChange={(e) => handleProjectChange(e)}
                label={t('project.project')}
                emptyOption={false}
                disabled={
                  !isPermissionsToEdit ||
                  isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                  updatesPending ||
                  sentToApproval
                }
              />
            </Grid>
            <Grid item xs={12} className={classes.gridItem}>
              <TypeChange
                handleChange={handleTypeChange}
                handleSubTypeChange={handleSubTypeChange}
                selectedType={type}
                disabled={
                  !isPermissionsToEdit ||
                  isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                  updatesPending ||
                  sentToApproval
                }
                subType={subType}
              ></TypeChange>
            </Grid>
            {type === 'RIDE' && (
              <Grid item xs={12} className={classes.gridItem}>
                <CountrySelect
                  changeCountry={(country) =>
                    setAttributes({ targetCountry: country })
                  }
                  label={t('record.country')}
                  propsCountry={targetCountry}
                  fullWidth
                  disabled={
                    !isPermissionsToEdit ||
                    isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                    updatesPending ||
                    sentToApproval
                  }
                />
              </Grid>
            )}
            <Grid item xs={12} sm={9} className={classes.gridItem}>
              <ProjectResourcesSelect
                projectId={project}
                setAttributes={setAttributes}
                isPermissionsToEdit={isPermissionsToEdit}
                isRecordApprovedAndUserNotAdmin={
                  isRecordApprovedAndUserNotWithBusinessPermissionsToEdit
                }
                updatesPending={updatesPending}
                sentToApproval={sentToApproval}
                selectedResourceId={resource}
              ></ProjectResourcesSelect>
            </Grid>
            {isPermissionToAddEmployees && isPermissionsToEdit ? (
              <Grid
                item
                xs={12}
                sm={3}
                className={classes.gridItem}
                style={{ display: 'flex', gap: '5px' }}
              >
                <OpenJoinEmployeeToProjectDialogButton
                  projectId={project}
                ></OpenJoinEmployeeToProjectDialogButton>
              </Grid>
            ) : null}
            {type === 'WORK' && (
              <>
                <Grid item xs={4} className={classes.gridItem}>
                  <TextInput
                    value={latIn ? 'Saatu' : 'Ei saatu'}
                    fullWidth
                    label={'Sijainti sisään'}
                    multiline
                    disabled={true}
                  />
                </Grid>
                {clockOutAt && (
                  <Grid item xs={4} className={classes.gridItem}>
                    <TextInput
                      value={latOut ? 'Saatu' : 'Ei saatu'}
                      fullWidth
                      label={'Sijainti ulos'}
                      multiline
                      disabled={true}
                    />
                  </Grid>
                )}
                <Grid item xs={4} className={classes.gridItem}>
                  <Button
                    className={classes.actionBtn}
                    onClick={() => setMapOpen(true)}
                    outline
                    color="warning"
                    label={'Paikkatieto'}
                    disabled={
                      (latIn && longIn) || (latOut && longOut) ? false : true
                    }
                  >
                    {'Avaa kartta'}
                  </Button>
                </Grid>
              </>
            )}
            <Grid item xs={12} className={classes.gridItem}>
              <TextInput
                fullWidth
                id="description"
                onChange={(e) => debounceDescriptionSearch(e.target.value)}
                label={t('calendar.description')}
                multiline
                disabled={
                  !isPermissionsToEdit ||
                  isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                  updatesPending ||
                  sentToApproval
                }
                defaultValue={description}
              />
            </Grid>
            <Grid item xs={12} className={classes.gridItem}>
              <FormControl fullWidth>
                <AssignmentsSelect
                  a1={
                    projectAssignments && projectAssignments[0]
                      ? projectAssignments[0]
                      : undefined
                  }
                  disabled={
                    !isPermissionsToEdit ||
                    isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                    updatesPending ||
                    sentToApproval
                  }
                  selectedAssignment={assignment}
                  assignments={projectAssignments}
                  update={handleAssignmentChange}
                  unitAmountDialogOpen={unitAmountDialogOpen}
                  setDialogOpen={setAssignmentUnitAmountDialogOpen}
                  setAssignmentUnitAmount={setAssignmentUnitAmount}
                  unitAmount={assignmentUnitAmount}
                />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <Divider />

        <DialogActions className={classes.dialogActions}>
          {isPermissionsToEdit ? (
            <Button
              className={clsx(classes.actionBtn, classes.actionBtnDelete)}
              color="danger"
              onClick={() => setRemoveRecordDialog(true)}
              disabled={
                isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
                updatesPending ||
                sentToApproval
              }
            >
              {t('common.remove')}
            </Button>
          ) : null}
          {history?.location?.pathname?.includes('calendar') &&
          type !== 'ABSENCE' ? (
            <Button
              onClick={() =>
                dispatch(copyRecordAsNew({ ...dialogData, id: undefined }))
              }
            >
              {t('record.copy_as_new')}
            </Button>
          ) : null}
          <Button
            className={classes.actionBtn}
            onClick={openHistory}
            outline
            color="primary"
          >
            {t('record.history')}
          </Button>

          <Button
            className={classes.actionBtn}
            onClick={closeDialog}
            outline
            color="primary"
          >
            {t('common.close')}
          </Button>

          <Button
            className={classes.actionBtn}
            onClick={handleSave}
            color="primary"
            disabled={
              clockedInGreaterThanClockedOut ||
              billedClockInGreaterThanClockout ||
              !resource ||
              !isPermissionsToEdit ||
              isRecordApprovedAndUserNotWithBusinessPermissionsToEdit ||
              updatesPending ||
              sentToApproval
            }
          >
            {!recordEditPending ? t('common.save') : <ButtonLoading />}
          </Button>
        </DialogActions>
        <Dialog
          classes={{ paper: classes.dialogPaper }}
          open={removeRecordDialog}
          onClose={() => setRemoveRecordDialog(false)}
          aria-labelledby="contact-delete-dialog"
          maxWidth="sm"
        >
          <DialogHeader
            icon={<CalendarRemoveIcon />}
            onClose={() => setRemoveRecordDialog(false)}
            title={t('calendar.remove_record')}
          />
          <DialogContent className={classes.removeRecordDialogContent}>
            <DialogContentText>
              {t('calendar.remove_record_confirmation')}
            </DialogContentText>
          </DialogContent>
          <Divider />
          <DialogActions className={classes.dialogActions}>
            <Button
              onClick={() => setRemoveRecordDialog(false)}
              color="primary"
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={handleRecordDelete} color="primary">
              {t('common.remove')}
            </Button>
          </DialogActions>
        </Dialog>
        {mapOpen && (
          <MapDialog
            onClose={() => setMapOpen(false)}
            records={[dialogData]}
            projects={[
              projectOptions.find(
                (projectOption) => projectOption.id === project
              ),
            ]}
          ></MapDialog>
        )}
      </Dialog>
    </>
  );
}

export default EditRecordDialog;
